/**
 * CLI Credential Commands
 *
 * List, inspect, and update credentials for workflow authoring.
 *
 * @module credential
 */

import process from 'node:process';
import { writeRawOutput } from './brand.js';
import { createParser, type EventSourceMessage } from 'eventsource-parser';
import { resolveConfig, getArgValue, hasFlag } from './auth.js';
import { apiRequest, fetchProperties, getApplicationArg, type CredentialListItem } from './cli-helpers.js';

/**
 * Parse repeatable `--variable NAME=value` flags into a persona-variable value map.
 * These fill the bound persona's declared variables; injected at run time as `$var.USER.<name>`.
 */
function parseVariableValues(argv: string[]): Record<string, string> {
  const values: Record<string, string> = {};
  for (let i = 0; i < argv.length; i++) {
    if (argv[i] !== '--variable') continue;
    const raw = argv[i + 1];
    if (!raw || raw.startsWith('-')) continue;
    const eq = raw.indexOf('=');
    if (eq <= 0) continue;
    values[raw.slice(0, eq)] = raw.slice(eq + 1);
  }
  return values;
}
import { apiFetch, exitOnFailure } from './api-fetch.js';

async function fetchCredentials(apiUrl: string, token: string): Promise<CredentialListItem[]> {
  const result = await apiFetch(`${apiUrl}/org/credentials`, {
    headers: { Authorization: `Bearer ${token}` },
    token,
  });

  if (!result.ok) exitOnFailure(result.failure);

  const data = (await result.response.json()) as { ok: boolean; items: CredentialListItem[] };
  return data.items ?? [];
}

function printCredentialHelp(): void {
  console.log(
    [
      'Usage: canary credential <sub-command> [options]',
      '',
      'Sub-commands:',
      '  list [options]                  List credentials with environment info',
      '  get <credId|name> [options]     Print credential login bundle (URL + instructions + username/password)',
      '  create [options]                Create a new credential',
      '  update <credId> [options]       Update credential fields',
      '  test <credId> [options]         Run the browser login test for a credential',
      '',
      'List options:',
      '  --application <name>  Filter by application name (alias: --property)',
      '  --json               Output raw JSON',
      '',
      'Create options:',
      '  --application <name|id>      Application to attach to (required; alias: --property)',
      '  --name <name>                Credential name (required)',
      '  --login-instructions <text>  Login instructions',
      '  --username <user>            Username (stored encrypted)',
      '  --password <pw>              Password (or set CANARY_CRED_PASSWORD)',
      '  --api-key <key>              API key (or set CANARY_CRED_API_KEY)',
      '  --mfa-secret <secret>        MFA seed (or set CANARY_CRED_MFA_SECRET)',
      '  --variable NAME=value        Fill a bound persona variable (repeatable; $var.USER.<NAME>)',
      '  --json                       Output raw JSON',
      '',
      'Update options:',
      '  --name <name>              Set the credential name',
      '  --role <role>              Set the role (admin, user, viewer, custom)',
      '  --login-instructions <text>  Set login instructions',
      '  --variable NAME=value      Fill a bound persona variable (repeatable; $var.USER.<NAME>)',
      '  --json                     Output raw JSON',
      '',
      'Note: the login URL is no longer set per credential. Configure the relative',
      'login path on the application (canary application update --login-path /login)',
      'or per environment; it is appended to the environment URL at run time.',
      '',
      'Test options:',
      '  --application <name|id>    Disambiguate when <credId> is a name (alias: --property)',
      '  --json                     Stream raw SSE events as JSON',
      '',
      'Common options:',
      '  --env <env>          Target environment (prod, dev, local)',
      '  --api-url <url>      API URL override',
      '  --token <key>        API token override',
    ].join('\n')
  );
}

async function handleList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const jsonFlag = hasFlag(argv, '--json');
  const propertyFilter = getApplicationArg(argv);

  let items = await fetchCredentials(apiUrl, token);

  if (propertyFilter) {
    const lower = propertyFilter.toLowerCase();
    items = items.filter(
      (c) =>
        c.propertyId === propertyFilter ||
        (c.propertyName && c.propertyName.toLowerCase().includes(lower))
    );
  }

  // Filter out ephemeral credentials for cleaner output
  items = items.filter((c) => !(c as unknown as { isEphemeral?: boolean }).isEphemeral);

  if (jsonFlag) {
    console.log(JSON.stringify(items, null, 2));
    return;
  }

  if (items.length === 0) {
    console.log('No credentials found.');
    return;
  }

  console.log(`\nCredentials (${items.length}):\n`);
  for (const c of items) {
    const property = c.propertyName ?? c.propertyId;
    const env = c.environmentName ? ` | ${c.environmentName}` : '';
    const baseUrl = c.propertyBaseUrl ? ` | ${c.propertyBaseUrl}` : '';
    console.log(`  ${c.name}`);
    console.log(`    ID: ${c.id}`);
    console.log(`    Application: ${property}${env}${baseUrl}`);
    console.log('');
  }
}

async function handleUpdate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const credId = argv.find((a) => !a.startsWith('-'));
  if (!credId) {
    console.error('Error: Missing credential ID.');
    console.error('Usage: canary credential update <credId> [--name <name>] [--role <role>] [--login-instructions <text>]');
    process.exit(1);
  }

  if (getArgValue(argv, '--login-url') !== undefined) {
    console.error(
      'Error: --login-url has been removed. The login URL now derives from the application:\n' +
        '  canary application update <app> --login-path /login'
    );
    process.exit(1);
  }

  const name = getArgValue(argv, '--name');
  const role = getArgValue(argv, '--role');
  const loginInstructions = getArgValue(argv, '--login-instructions');
  const variableValues = parseVariableValues(argv);
  const hasVariableValues = Object.keys(variableValues).length > 0;
  const jsonOutput = hasFlag(argv, '--json');

  if (!name && !role && !loginInstructions && !hasVariableValues) {
    console.error(
      'Error: No fields to update. Provide at least one of: --name, --role, --login-instructions, --variable NAME=value'
    );
    process.exit(1);
  }

  const validRoles = ['admin', 'user', 'viewer', 'custom'];
  if (role && !validRoles.includes(role)) {
    console.error(`Error: Invalid role "${role}". Must be one of: ${validRoles.join(', ')}`);
    process.exit(1);
  }

  // Auto-resolve propertyId from credential list
  const credentials = await fetchCredentials(apiUrl, token);
  const credential = credentials.find((c) => c.id === credId);
  if (!credential) {
    console.error(`Error: Credential ${credId} not found.`);
    if (credentials.length > 0) {
      console.error('\nAvailable credentials:');
      for (const c of credentials.slice(0, 10)) {
        console.error(`  ${c.id}  ${c.name}  (${c.propertyName ?? c.propertyId})`);
      }
    }
    process.exit(1);
  }

  const body: Record<string, unknown> = {};
  if (name !== undefined) body.name = name;
  if (role !== undefined) body.role = role;
  if (loginInstructions !== undefined) body.loginInstructions = loginInstructions;
  if (hasVariableValues) body.variableValues = variableValues;

  const result = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl,
    token,
    'PUT',
    `/org/properties/${credential.propertyId}/credentials/${credId}`,
    body
  );

  if (!result.ok) {
    if (jsonOutput) {
      console.log(JSON.stringify({ ok: false, error: result.error ?? 'Unknown error' }, null, 2));
    } else {
      console.error(`Error: ${result.error ?? 'Unknown error'}`);
    }
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify({ ok: true, credentialId: credId, updated: body }, null, 2));
    return;
  }

  console.log(`Updated "${credential.name}" (${credId}):`);
  if (name !== undefined) {
    console.log(`  name: ${credential.name} -> ${name}`);
  }
  if (role !== undefined) {
    console.log(`  role: -> ${role}`);
  }
  if (loginInstructions !== undefined) {
    console.log(`  loginInstructions: updated`);
  }
}

async function handleCreate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const jsonOutput = hasFlag(argv, '--json');
  const propertyArg = getApplicationArg(argv);
  const name = getArgValue(argv, '--name');
  if (getArgValue(argv, '--login-url') !== undefined) {
    console.error(
      'Error: --login-url has been removed. The login URL now derives from the application:\n' +
        '  canary application update <app> --login-path /login'
    );
    process.exit(1);
  }
  const loginInstructions = getArgValue(argv, '--login-instructions');
  const username = getArgValue(argv, '--username');
  const password = getArgValue(argv, '--password') ?? process.env.CANARY_CRED_PASSWORD;
  const apiKey = getArgValue(argv, '--api-key') ?? process.env.CANARY_CRED_API_KEY;
  const mfaSecret = getArgValue(argv, '--mfa-secret') ?? process.env.CANARY_CRED_MFA_SECRET;

  if (!propertyArg || !name) {
    console.error('Error: --application and --name are required (alias: --property).');
    console.error('Usage: canary credential create --application <name|id> --name <name> [options]');
    process.exit(1);
  }

  const properties = await fetchProperties(apiUrl, token);
  const property = properties.find(
    (p) => p.id === propertyArg || p.name.toLowerCase() === propertyArg.toLowerCase()
  );
  if (!property) {
    console.error(`Error: Application "${propertyArg}" not found.`);
    if (properties.length > 0) {
      console.error('Available applications:');
      for (const p of properties) console.error(`  - ${p.name} (${p.id})`);
    }
    process.exit(1);
  }

  const data: Record<string, unknown> = {};
  if (username !== undefined) data.username = username;
  if (password !== undefined) data.password = password;
  if (apiKey !== undefined) data.apiKey = apiKey;
  if (mfaSecret !== undefined) data.mfaSecret = mfaSecret;

  const body: Record<string, unknown> = { name, data };
  if (loginInstructions !== undefined) body.loginInstructions = loginInstructions;
  const variableValues = parseVariableValues(argv);
  if (Object.keys(variableValues).length > 0) body.variableValues = variableValues;

  const result = await apiRequest<{
    ok: boolean;
    id?: string;
    credential?: { id: string };
    error?: string;
  }>(apiUrl, token, 'POST', `/org/properties/${property.id}/credentials`, body);

  if (!result.ok) {
    if (jsonOutput) {
      console.log(JSON.stringify(result, null, 2));
    } else {
      console.error(`Error: ${result.error ?? 'Unknown error'}`);
    }
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  console.log(`Created credential "${name}" (${result.id}) on application "${property.name}"`);
}

type CredentialDetail = {
  id: string;
  name: string;
  authProvider?: string;
  /** Resolved login start URL (environment URL + relative login path) */
  loginUrl?: string | null;
  loginInstructions?: string | null;
  data?: {
    username?: string;
    password?: string;
    email?: string;
    user?: string;
    mfaSecret?: string;
    apiKey?: string;
    customHeaders?: Record<string, string>;
    [key: string]: unknown;
  } | null;
};

async function fetchCredentialDetail(
  apiUrl: string,
  token: string,
  propertyId: string,
  credId: string
): Promise<CredentialDetail | null> {
  const result = await apiRequest<{ ok: boolean; credential?: CredentialDetail; error?: string }>(
    apiUrl,
    token,
    'GET',
    `/org/properties/${propertyId}/credentials/${credId}`
  );
  if (!result.ok || !result.credential) return null;
  return result.credential;
}

async function handleGet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const jsonOutput = hasFlag(argv, '--json');
  const propertyArg = getApplicationArg(argv);
  const credArg = argv.find((a) => !a.startsWith('-'));

  if (!credArg) {
    console.error('Error: Missing credential ID or name.');
    console.error('Usage: canary credential get <credId|name> [--application <name|id>] [--json]');
    process.exit(1);
  }

  const credentials = await fetchCredentials(apiUrl, token);
  const candidates = credentials.filter(
    (c) => c.id === credArg || c.name.toLowerCase() === credArg.toLowerCase()
  );

  let credSummary: CredentialListItem | undefined;
  if (candidates.length === 0) {
    console.error(`Error: Credential "${credArg}" not found.`);
    process.exit(1);
  } else if (candidates.length === 1) {
    credSummary = candidates[0];
  } else {
    if (!propertyArg) {
      console.error(`Error: Multiple credentials match "${credArg}". Pass --application to disambiguate.`);
      for (const c of candidates) {
        console.error(`  - ${c.id}  (${c.propertyName ?? c.propertyId})`);
      }
      process.exit(1);
    }
    const lower = propertyArg.toLowerCase();
    credSummary = candidates.find(
      (c) => c.propertyId === propertyArg || (c.propertyName && c.propertyName.toLowerCase() === lower)
    );
    if (!credSummary) {
      console.error(`Error: No credential named "${credArg}" on application "${propertyArg}".`);
      process.exit(1);
    }
  }

  const detail = await fetchCredentialDetail(apiUrl, token, credSummary.propertyId, credSummary.id);
  if (!detail) {
    console.error(`Error: Failed to load credential ${credSummary.id}.`);
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify(detail, null, 2));
    return;
  }

  const data = detail.data ?? {};
  const username = data.username ?? data.email ?? data.user ?? '';
  writeRawOutput(`Credential: ${detail.name}  (${detail.id})`);
  writeRawOutput(`  Application:          ${credSummary.propertyName ?? credSummary.propertyId}`);
  writeRawOutput(`  Auth provider:        ${detail.authProvider ?? '(unknown)'}`);
  writeRawOutput(`  Login URL:            ${detail.loginUrl ?? '(not set)'}`);
  if (username) writeRawOutput(`  Username:             ${username}`);
  if (data.password) writeRawOutput(`  Password:             ${data.password}`);
  if (data.mfaSecret) writeRawOutput(`  MFA secret:           ${data.mfaSecret}`);
  console.log('');
  console.log('  Login instructions:');
  if (detail.loginInstructions) {
    for (const line of detail.loginInstructions.split('\n')) {
      writeRawOutput(`    ${line}`);
    }
  } else {
    console.log('    (none)');
  }
}

async function handleTest(argv: string[], apiUrl: string, token: string): Promise<void> {
  const jsonOutput = hasFlag(argv, '--json');
  const propertyArg = getApplicationArg(argv);
  const credArg = argv.find((a) => !a.startsWith('-'));

  if (!credArg) {
    console.error('Error: Missing credential ID or name.');
    console.error('Usage: canary credential test <credId> [--application <name|id>]');
    process.exit(1);
  }

  const credentials = await fetchCredentials(apiUrl, token);
  const candidates = credentials.filter(
    (c) => c.id === credArg || c.name.toLowerCase() === credArg.toLowerCase()
  );
  let credSummary: CredentialListItem | undefined;
  if (candidates.length === 0) {
    console.error(`Error: Credential "${credArg}" not found.`);
    process.exit(1);
  } else if (candidates.length === 1) {
    credSummary = candidates[0];
  } else {
    if (!propertyArg) {
      console.error(`Error: Multiple credentials match "${credArg}". Pass --application to disambiguate.`);
      for (const c of candidates) {
        console.error(`  - ${c.id}  (${c.propertyName ?? c.propertyId})`);
      }
      process.exit(1);
    }
    const lower = propertyArg.toLowerCase();
    credSummary = candidates.find(
      (c) => c.propertyId === propertyArg || (c.propertyName && c.propertyName.toLowerCase() === lower)
    );
    if (!credSummary) {
      console.error(`Error: No credential named "${credArg}" on application "${propertyArg}".`);
      process.exit(1);
    }
  }

  const detail = await fetchCredentialDetail(apiUrl, token, credSummary.propertyId, credSummary.id);
  if (!detail) {
    console.error(`Error: Failed to load credential ${credSummary.id}.`);
    process.exit(1);
  }

  if (detail.authProvider === 'api_key') {
    console.error(
      `Error: Credential "${detail.name}" uses the api_key auth provider. The browser login test is only valid for browser credentials.`
    );
    process.exit(1);
  }

  // A minted-session credential has no login UI for the agent to drive — its
  // session is built server-side from a refresh token. Running the AI login
  // test against it would fail for the wrong reason.
  if (detail.authProvider === 'google_oauth') {
    console.error(
      `Error: Credential "${detail.name}" is a Google (token) credential. Its session is minted from a refresh token and injected directly, so there is no login flow to test. Run a workflow using this credential instead.`
    );
    process.exit(1);
  }

  const startRes = await apiRequest<{ ok: boolean; streamId?: string; error?: string }>(
    apiUrl,
    token,
    'POST',
    `/org/properties/${credSummary.propertyId}/credentials/${credSummary.id}/mcp-test/browser-stream`
  );

  if (!startRes.ok || !startRes.streamId) {
    console.error(`Error: ${startRes.error ?? 'Failed to start browser login test'}`);
    process.exit(1);
  }

  const { streamId } = startRes;
  if (!jsonOutput) {
    console.log(`Starting browser login test for "${detail.name}" (stream ${streamId})...`);
  }

  const streamRes = await fetch(`${apiUrl}/browser-stream/${streamId}/stream`, {
    headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
  });

  if (!streamRes.ok || !streamRes.body) {
    console.error(`Failed to connect to event stream: ${streamRes.status}`);
    process.exit(1);
  }

  let exitCode = 0;
  let done = false;

  const parser = createParser({
    onEvent: (event: EventSourceMessage) => {
      if (!event.data) return;
      let data: unknown;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      if (jsonOutput) {
        console.log(JSON.stringify({ event: event.event, data }));
      } else if (event.event === 'browser.action') {
        const action = data as { type?: string; description?: string };
        console.log(`  → ${action.type ?? 'action'}${action.description ? `: ${action.description}` : ''}`);
      } else if (event.event === 'browser.status') {
        const status = data as { status?: string; message?: string };
        if (status.status) {
          console.log(`[${status.status}]${status.message ? ` ${status.message}` : ''}`);
        }
      }

      if (event.event === 'browser.status') {
        const status = data as { status?: string };
        if (status.status === 'disconnected' || status.status === 'error') {
          done = true;
          if (status.status === 'error') exitCode = 1;
        }
      }
    },
  });

  const reader = streamRes.body.getReader();
  const decoder = new TextDecoder();
  while (!done) {
    const { value, done: streamDone } = await reader.read();
    if (streamDone) break;
    parser.feed(decoder.decode(value, { stream: true }));
  }

  process.exit(exitCode);
}

export async function runCredential(argv: string[]): Promise<void> {
  const [subCommand, ...rest] = argv;

  if (!subCommand || subCommand === 'help' || hasFlag(argv, '--help', '-h')) {
    printCredentialHelp();
    return;
  }

  const { apiUrl, token } = await resolveConfig(rest);

  switch (subCommand) {
    case 'list':
      await handleList(rest, apiUrl, token);
      return;
    case 'get':
      await handleGet(rest, apiUrl, token);
      return;
    case 'create':
      await handleCreate(rest, apiUrl, token);
      return;
    case 'update':
      await handleUpdate(rest, apiUrl, token);
      return;
    case 'test':
      await handleTest(rest, apiUrl, token);
      return;
    default:
      console.error(`Unknown credential sub-command: ${subCommand}`);
      printCredentialHelp();
      process.exit(1);
  }
}
